// frontend/src/pages/AnalysisPage/sections/FileSelection.js
// Purpose: Allow selection of a file for analysis

// Desired outcome: Provide a dropdown of previously uploaded files that
// the user can select to begin a new analysis

import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  selectUploadedFiles, 
  selectProgress, 
  selectFile,
  fetchUploadedFiles,
  clearError
} from '../../../state/analysis/analysisSlice';
import logger from '../../../utils/logger';
import './sections.css';

const FileSelection = () => {
  const dispatch = useDispatch();
  const uploadedFiles = useSelector(selectUploadedFiles);
  const progress = useSelector(selectProgress);
  const [selectedFileID, setSelectedFileID] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Use a ref to prevent repeated fetch
  const hasInitialized = useRef(false);
  const isSubmitting = useRef(false);

  // Fetch uploaded files only on first render
  useEffect(() => {
    // If we already have files or have already triggered a fetch, don't fetch again
    if (hasInitialized.current || uploadedFiles.length > 0) {
      setIsLoading(false);
      return;
    }
    
    // Mark as initialized immediately to prevent any possibility of multiple calls
    hasInitialized.current = true;
    
    dispatch(fetchUploadedFiles())
      .unwrap()
      .then(() => {
        setIsLoading(false);
        logger.info('Files loaded successfully for selection');
      })
      .catch(err => {
        setIsLoading(false);
        setError(err.message || 'Error fetching files');
        logger.error('Error loading files for selection', err);
      });
  }, [dispatch, uploadedFiles.length]);

  // Handle file selection
  const handleFileChange = (e) => {
    setSelectedFileID(e.target.value);
    // Clear any previous errors when user makes a new selection
    if (error) {
      setError(null);
      dispatch(clearError());
    }
  };

  // Handle submission of selected file
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    
    if (!selectedFileID) {
      setError('Please select a file to continue');
      isSubmitting.current = false;
      return;
    }
    
    dispatch(selectFile(selectedFileID))
      .unwrap()
      .then(() => {
        logger.info(`File selected for analysis: ${selectedFileID}`);
        // Reset submission flag with delay to prevent rapid re-triggers
        setTimeout(() => {
          isSubmitting.current = false;
        }, 500);
      })
      .catch(err => {
        setError(err.message || 'Error selecting file');
        logger.error(`Error selecting file: ${selectedFileID}`, err);
        isSubmitting.current = false;
      });
  };

  // Format date in a readable way
  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Rendering states
  if (isLoading || progress.status === 'loading') {
    return <div className="loading-indicator">Loading files...</div>;
  }

  return (
    <div className="file-selection-section">
      <h2>Select File for Analysis</h2>
      
      {error && (
        <div className="error-message">
          {error}
          <button 
            className="dismiss-button" 
            onClick={() => {
              setError(null);
              dispatch(clearError());
            }}
            style={{ marginLeft: '10px' }}
          >
            Dismiss
          </button>
        </div>
      )}
      
      {uploadedFiles.length === 0 ? (
        <div className="no-files-message">
          <p>No files available for analysis. Please upload a file first.</p>
          <button 
            className="primary-button"
            onClick={() => window.location.href = '/upload'}
          >
            Go to Upload Page
          </button>
        </div>
      ) : (
        <form className="file-selection-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="fileSelect">Choose a file:</label>
            <select 
              id="fileSelect" 
              value={selectedFileID} 
              onChange={handleFileChange}
              className="file-select"
            >
              <option value="">-- Select a file --</option>
              {uploadedFiles.map((file) => (
                <option key={file.fileID} value={file.fileID}>
                  {file.originalName} ({formatDate(file.uploadDate)})
                </option>
              ))}
            </select>
          </div>
          
          <button 
            type="submit" 
            className="primary-button"
            disabled={!selectedFileID || progress.status === 'loading' || isSubmitting.current}
          >
            {progress.status === 'loading' ? 'Processing...' : 'Continue with Selected File'}
          </button>
        </form>
      )}
    </div>
  );
};

export default FileSelection;